import MainHeaderComponent from '../../components/main-header/main-header.component';
import { Loading } from 'carbon-components-react';
import { MoreVertical, Plus } from 'react-feather';
import { useState } from 'react';
import './daily-fee.styles.scss';
import {
  formatDate,
  getStatusColor,
  hasExpired,
  redirectToBillingService,
} from '../../utils/common.util';
import {
  CreateFeeType,
  CreateGroupFeeType,
  EditFeeType,
  EditGroupFeeType,
} from './FeeTypeForm';
import { useHistory } from 'react-router-dom';
import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs';
import { ConfirmModal } from '../../components/modal/confirm-modal/ConfirmModal.component';
import useSWR from 'swr';
import { useIsAllowed } from '../../utils/useCurrentUser';
import Dropdown from '../../components/dropdown/Dropdown';
import { useCustomMutation } from '../../utils/useCustomMutation';
import { deleteMethod, postMethod } from '../../api/common';

export default function FeeTypePage() {
  const { isAllowed } = useIsAllowed();

  const {
    data: feeTypes,
    isLoading,
    mutate: refetchData,
  } = useSWR(`daily-billing/fees/list`);
  const data = feeTypes?.dailyBillingFee ?? [];

  const [isOpen, setIsOpen] = useState('');

  const add_fee_type = isAllowed(['DailyBillingFeesTypeCreationAccess']);

  const actions = [
    { label: 'Class Based', onClick: () => setIsOpen('classFeeType') },
    {
      label: (
        <span title="Instead of class, its based on a group of your own definition e.g grouping students based on location, etc.">
          Group Based
        </span>
      ),
      onClick: () => setIsOpen('groupFeeType'),
    },
  ];

  const feeTypeKind = {
    standard: 'Class Based',
    generic: 'Group Based',
  };

  const getStatus = (isDeactivated) => {
    if (isDeactivated) return 'Inactive';
    return 'Active';
  };

  return (
    <div>
      <MainHeaderComponent />

      <CreateFeeType
        isOpen={isOpen === 'classFeeType'}
        closeModal={() => setIsOpen('')}
      />

      <CreateGroupFeeType
        isOpen={isOpen === 'groupFeeType'}
        closeModal={() => setIsOpen('')}
      />

      <div className=" page-container fee-type-page">
        <Breadcrumbs
          items={[
            { label: 'Daily Fees', to: '/daily-fees' },
            { label: 'Fee Types' },
          ]}
        />

        <hr className="grey-hr mt-210 mb-10" />

        <div className="mt-15 h-center" style={{ gap: '12px' }}>
          <span className="h-20">Fee Types</span>
          {add_fee_type && (
            <Dropdown actions={actions}>
              <button
                className="text-12 text-primary font-bold pointer clean-btn "
                style={{ gap: '4px' }}
              >
                <Plus size="11" strokeWidth="5px" />
                &nbsp;
                <span>ADD NEW FEE TYPE</span>
              </button>
            </Dropdown>
          )}
        </div>

        {/* <div className="mt-10">
          <Search
            disabled={isLoading}
            id="search"
            name="search"
            labelText=""
            value={query.search}
            onChange={(e) => setQuery({ query: e.target.value })}
            placeHolderText="Search by Cheque Number, School, Amount Paid or Date"
          />
        </div> */}

        <p className="text-14 mt-30">
          Click on an action cell for a fee type to carry out actions.
        </p>

        <div className="mt-20 fade-in" hidden={!isLoading}>
          <Loading small={true} withOverlay={false} description="Loading..." />
        </div>

        <p
          className="text-14 mt-30 text-grey"
          hidden={isLoading || data.length > 0}
        >
          No fee types created yet
        </p>

        <div className="nm mt-20" hidden={isLoading || data.length === 0}>
          <table className="bx--data-table table-white">
            <thead>
              <tr>
                <th>Name</th>
                <th>Base Amount (GHS)</th>
                <th>Type</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((fee) => {
                return (
                  <tr key={fee.id}>
                    <td>{fee.name}</td>
                    <td>{fee.amount}</td>
                    <td>{feeTypeKind[fee.type.toLowerCase()]}</td>
                    <td
                      className={getStatusColor(getStatus(fee.isDeactivated))}
                    >
                      {getStatus(fee.isDeactivated)}
                    </td>
                    <td>
                      <ActionsCell data={fee} refetchData={refetchData} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ActionsCell({ data, refetchData }) {
  const history = useHistory();

  const [isOpen, setIsOpen] = useState('');

  function checkInvoice(action) {
    if (hasExpired(data?.invoice?.gracePeriodEndDate)) {
      setIsOpen('unpaid');
    } else if (action) {
      setIsOpen(action);
    } else {
      data?.type === 'Generic'
        ? history.push(`/daily-fees/group-types/${data?.id}`)
        : history.push(`/daily-fees/types/${data?.id}`);
    }
  }

  const { trigger, error, isMutating, reset } = useCustomMutation(
    `daily-billing/fees/${data?.id}/delete`,
    (url, { arg }) => deleteMethod(url, arg),
  );

  const {
    trigger: toggleActivation,
    error: activationError,
    isMutating: isActivating,
    reset: resetActivation,
  } = useCustomMutation(
    `daily-billing/fees/${data?.id}/toggle-activation`,
    (url, { arg }) => postMethod(url, arg),
  );

  const statusText = data?.isDeactivated ? 'Activate' : 'Deactivate';
  const actions = [
    { label: 'View', onClick: () => checkInvoice() },
    { label: 'Edit', onClick: () => checkInvoice('edit') },
    { label: statusText, onClick: () => setIsOpen('status') },
    { label: 'Delete', onClick: () => setIsOpen('delete') },
  ];

  return (
    <>
      <Dropdown actions={actions}>
        <span style={{ cursor: 'pointer' }}>
          <MoreVertical />
        </span>
      </Dropdown>

      <ConfirmModal
        title={`${statusText} Fee Type: ${data?.name}`}
        isOpen={isOpen === 'status'}
        closeModal={() => {
          setIsOpen('');
          resetActivation();
        }}
        secondaryActionText="Close"
        description={`Are you sure you want to ${statusText.toLowerCase()} this fee type?`}
        actionText={statusText.toUpperCase()}
        actionBtnClasses={statusText === 'Activate' ? 'success' : 'danger'}
        onClick={() =>
          toggleActivation(null, {
            onSuccess() {
              setIsOpen('');
              refetchData();
              resetActivation();
            },
          })
        }
        isLoading={isActivating}
        errorMessage={activationError ? activationError.message : null}
      />

      <ConfirmModal
        title={`Delete Fee Type: ${data?.name}`}
        isOpen={isOpen === 'delete'}
        closeModal={() => {
          setIsOpen('');
          reset();
        }}
        secondaryActionText="Close"
        description="Are you sure you want to delete this fee type? This action cannot be undone."
        actionText="DELETE"
        actionBtnClasses="danger"
        onClick={() =>
          trigger(null, {
            onSuccess() {
              setIsOpen('');
              refetchData();
              reset();
            },
          })
        }
        isLoading={isMutating}
        errorMessage={error ? error.message : null}
      />

      <ConfirmModal
        title={`Unpaid Invoice for ${data?.name}`}
        isOpen={isOpen === 'unpaid'}
        closeModal={() => setIsOpen('')}
        secondaryActionText="Close"
        description={
          <>
            <div className="mt-20">
              <p className="text-danger">There is an unpaid invoice</p>
              <div className="mt-5" style={{ display: 'grid' }}>
                <div
                  style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                  <span className="text-12">Invoice Number: </span>
                  <span>{data?.invoice?.invoiceNumber}</span>
                </div>
                <div
                  style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                  <span className="text-12">Due Date: </span>
                  <span>{formatDate(data?.invoice?.calculatedDueDate)}</span>
                </div>
                <div
                  style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                  <span className="text-12">Balance Due: </span>
                  <span className="text-danger">
                    GH₵{data?.invoice?.balanceDue}
                  </span>
                </div>
              </div>
            </div>
          </>
        }
        actionText="PAY NOW"
        actionBtnClasses="primary"
        onClick={() =>
          redirectToBillingService(`invoices/${data?.invoice?.id}`)
        }
      />

      <EditFeeType
        data={data}
        isOpen={isOpen === 'edit' && data?.type !== 'Generic'}
        closeModal={() => setIsOpen('')}
      />

      <EditGroupFeeType
        data={data}
        isOpen={isOpen === 'edit' && data?.type === 'Generic'}
        closeModal={() => setIsOpen('')}
      />
    </>
  );
}
// function FeeCard({ data, refetchData }) {
//   const history = useHistory();

//   const [isOpen, setIsOpen] = useState('');

//   function checkInvoice(action) {
//     if (hasExpired(data?.invoice?.gracePeriodEndDate)) {
//       setIsOpen('unpaid');
//     } else if (action) {
//       setIsOpen(action);
//     } else {
//       data?.type === 'Generic'
//         ? history.push(`/daily-fees/group-types/${data?.id}`)
//         : history.push(`/daily-fees/types/${data?.id}`);
//     }
//   }

//   const { trigger, error, isMutating, reset } = useCustomMutation(
//     `daily-billing/fees/${data?.id}/delete`,
//     (url, { arg }) => deleteMethod(url, arg),
//   );

//   return (
//     <>
//       <div
//         role="button"
//         tabIndex={0}
//         style={{ cursor: 'pointer' }}
//         className="fee-card"
//         onClick={() => checkInvoice()}
//         onKeyDown={() => checkInvoice()}
//       >
//         <div style={{ display: 'grid', gap: '16px' }}>
//           <span className="font-bold text-20">{data?.name}</span>
//           <span>{formatCurrencyV2(data?.amount)}</span>
//         </div>
//         <div className="d-flex gap-8 flex-col">
//           <button
//             onClick={(e) => {
//               e.stopPropagation();
//               checkInvoice('edit');
//             }}
//             className="clean-btn text-primary highlight"
//           >
//             <Edit size="16" strokeWidth="2px" />
//           </button>
//           <button
//             className="clean-btn text-danger highlight"
//             onClick={(e) => {
//               e.stopPropagation();
//               setIsOpen('delete');
//             }}
//           >
//             <Trash size="16" strokeWidth="2px" />
//           </button>
//         </div>
//       </div>

//       <ConfirmModal
//         title={`Delete Fee Type: ${data?.name}`}
//         isOpen={isOpen === 'delete'}
//         closeModal={() => {
//           setIsOpen('');
//           reset();
//         }}
//         secondaryActionText="Close"
//         description="Are you sure you want to delete this fee type? This action cannot be undone."
//         actionText="DELETE"
//         actionBtnClasses="danger"
//         onClick={() =>
//           trigger(null, {
//             onSuccess() {
//               setIsOpen('');
//               refetchData();
//               reset();
//             },
//           })
//         }
//         isLoading={isMutating}
//         errorMessage={error ? error.message : null}
//       />

//       <ConfirmModal
//         title={`Unpaid Invoice for ${data?.name}`}
//         isOpen={isOpen === 'unpaid'}
//         closeModal={() => setIsOpen('')}
//         secondaryActionText="Close"
//         description={
//           <>
//             <div className="mt-20">
//               <p className="text-danger">There is an unpaid invoice</p>
//               <div className="mt-5" style={{ display: 'grid' }}>
//                 <div
//                   style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
//                 >
//                   <span className="text-12">Invoice Number: </span>
//                   <span>{data?.invoice?.invoiceNumber}</span>
//                 </div>
//                 <div
//                   style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
//                 >
//                   <span className="text-12">Due Date: </span>
//                   <span>{formatDate(data?.invoice?.calculatedDueDate)}</span>
//                 </div>
//                 <div
//                   style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
//                 >
//                   <span className="text-12">Balance Due: </span>
//                   <span className="text-danger">
//                     GH₵{data?.invoice?.balanceDue}
//                   </span>
//                 </div>
//               </div>
//             </div>
//           </>
//         }
//         actionText="PAY NOW"
//         actionBtnClasses="primary"
//         onClick={() =>
//           redirectToBillingService(`invoices/${data?.invoice?.id}`)
//         }
//       />

//       <EditFeeType
//         data={data}
//         isOpen={isOpen === 'edit' && data?.type !== 'Generic'}
//         closeModal={() => setIsOpen('')}
//       />

//       <EditGroupFeeType
//         data={data}
//         isOpen={isOpen === 'edit' && data?.type === 'Generic'}
//         closeModal={() => setIsOpen('')}
//       />
//     </>
//   );
// }
